import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Tooltip } from "@air/primitive-tooltip";
import { Fragment, memo, useMemo } from "react";
import { CustomFieldListWithOverflow } from "~/components/CustomFields/CustomFieldListWithOverflow";
import { BoardGalleryCardMenu } from "~/components/Gallery/BoardGalleryCard/BoardGalleryCardMenu";
import { BoardGalleryCardThumbnail } from "~/components/Gallery/BoardGalleryCard/BoardGalleryCardThumbnail";
import { GalleryCard } from "~/components/Gallery/GalleryCard/GalleryCard";
import { GalleryCardContent } from "~/components/Gallery/GalleryCard/GalleryCardContent";
import { GalleryCardFooter } from "~/components/Gallery/GalleryCard/GalleryCardFooter";
import { GalleryCardImageWrapper } from "~/components/Gallery/GalleryCard/GalleryCardImageWrapper";
import { SelectedGalleryCardOverlay } from "~/components/Gallery/GalleryCard/SelectedGalleryCardOverlay";
import { GalleryCardCustomFieldsAction } from "~/components/Gallery/GalleryCardCustomFieldsAction";
import { GalleryItemType } from "~/components/Gallery/types";
import { BOARD_CARD, BOARD_CARD_TITLE, BOARD_META } from "~/constants/testIDs";
import { visibleCustomFieldsViewOptionsSelector } from "~/store/configViews/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
function _BoardGalleryCard(param) {
    let { board, cardWidth, getItemMenuOptions, getSelectionMenuOptions, isSelectable = true, libraryCustomFields, onClick, onCmdClickTitle, ancestors, sizes, canViewCustomFields, getButtonClassName } = param;
    const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
    const popoverCustomFields = useMemo(()=>{
        if (!!(libraryCustomFields === null || libraryCustomFields === void 0 ? void 0 : libraryCustomFields.length)) {
            var _board_customFields;
            return board === null || board === void 0 ? void 0 : (_board_customFields = board.customFields) === null || _board_customFields === void 0 ? void 0 : _board_customFields.filter((board)=>{
                var _libraryCustomFields_find;
                return !!((_libraryCustomFields_find = libraryCustomFields.find((customField)=>customField.id === board.id)) === null || _libraryCustomFields_find === void 0 ? void 0 : _libraryCustomFields_find.visible);
            });
        }
        return board.customFields;
    }, [
        board.customFields,
        libraryCustomFields
    ]);
    const boardCustomFields = useMemo(()=>{
        var _board_customFields;
        if (!!(libraryCustomFields === null || libraryCustomFields === void 0 ? void 0 : libraryCustomFields.length)) {
            var _board_customFields1;
            return (_board_customFields1 = board.customFields) === null || _board_customFields1 === void 0 ? void 0 : _board_customFields1.filter((boardField)=>{
                const libraryField = libraryCustomFields.find((customField)=>customField.id === boardField.id);
                const visibleField = visibleCustomFields.find((vcf)=>vcf.customFieldId === boardField.id);
                return (libraryField === null || libraryField === void 0 ? void 0 : libraryField.visible) && visibleField;
            });
        }
        return (_board_customFields = board.customFields) === null || _board_customFields === void 0 ? void 0 : _board_customFields.filter((customField)=>visibleCustomFields.find((vcf)=>vcf.customFieldId === customField.id));
    }, [
        board.customFields,
        libraryCustomFields,
        visibleCustomFields
    ]);
    const boardTitle = useMemo(()=>{
        /**
     * We break the title into parts and add a wbr tag between them to allow
     * the title to break at the underscore character.
     */ if (board.title.includes("_")) {
            return board.title.split("_").map((part, i, array)=>/*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        part,
                        i < array.length - 1 && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("wbr", {}),
                                "_",
                                /*#__PURE__*/ _jsx("wbr", {})
                            ]
                        })
                    ]
                }, i));
        }
        return board.title;
    }, [
        board.title
    ]);
    return /*#__PURE__*/ _jsx(GalleryCard, {
        testId: BOARD_CARD,
        title: board.title,
        getItemMenuOptions: getItemMenuOptions,
        getSelectionMenuOptions: getSelectionMenuOptions,
        item: {
            id: board.id,
            item: board,
            type: GalleryItemType.board
        },
        onClick: onClick,
        isSelectable: isSelectable,
        getButtonClassName: getButtonClassName,
        children: (param)=>{
            let { setIsMenuOpen, isHovering, isSelected } = param;
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(GalleryCardImageWrapper, {
                        children: board.thumbnails && board.thumbnails.length > 0 && /*#__PURE__*/ _jsx(BoardGalleryCardThumbnail, {
                            board: board,
                            sizes: sizes,
                            height: "100%"
                        })
                    }),
                    isSelected && /*#__PURE__*/ _jsx(SelectedGalleryCardOverlay, {}),
                    /*#__PURE__*/ _jsxs(GalleryCardContent, {
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex",
                                children: [
                                    canViewCustomFields && !!(popoverCustomFields === null || popoverCustomFields === void 0 ? void 0 : popoverCustomFields.length) && /*#__PURE__*/ _jsx("div", {
                                        className: "pointer-events-auto ml-2 mt-2",
                                        children: /*#__PURE__*/ _jsx(GalleryCardCustomFieldsAction, {
                                            customFields: popoverCustomFields
                                        })
                                    }),
                                    isHovering && /*#__PURE__*/ _jsx("div", {
                                        className: "pointer-events-auto mr-2 mt-2 flex grow justify-end",
                                        children: /*#__PURE__*/ _jsx(BoardGalleryCardMenu, {
                                            board: board,
                                            getItemMenuOptions: getItemMenuOptions,
                                            getSelectionMenuOptions: getSelectionMenuOptions,
                                            setIsMenuOpen: setIsMenuOpen
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(GalleryCardFooter, {
                                children: [
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        label: board.title,
                                        side: "top",
                                        sideOffset: 4,
                                        align: "start",
                                        children: /*#__PURE__*/ _jsx("button", {
                                            onClick: (event)=>{
                                                if ((event.metaKey || event.ctrlKey) && !!onCmdClickTitle) {
                                                    onCmdClickTitle();
                                                } else {
                                                    onClick();
                                                }
                                            },
                                            className: "pointer-events-auto text-left text-20 font-semibold leading-none text-white hover:underline",
                                            "data-testid": BOARD_CARD_TITLE,
                                            children: /*#__PURE__*/ _jsx("div", {
                                                /**
                     * We add a pb-0.5 to the line-clamp-3 to make sure that the
                     * text doesn't get cut off
                     */ className: "line-clamp-3 break-words pb-0.5",
                                                children: boardTitle
                                            })
                                        })
                                    }),
                                    !!(boardCustomFields === null || boardCustomFields === void 0 ? void 0 : boardCustomFields.length) && /*#__PURE__*/ _jsx("div", {
                                        className: "pointer-events-auto",
                                        children: /*#__PURE__*/ _jsx(CustomFieldListWithOverflow, {
                                            className: "mt-1 py-1",
                                            customFields: boardCustomFields,
                                            allowedWidth: cardWidth,
                                            hasDarkBackground: true,
                                            shouldShowTooltip: true
                                        })
                                    }),
                                    !!ancestors && /*#__PURE__*/ _jsx("p", {
                                        className: "mt-0.5 pr-7 text-12 text-white",
                                        "data-testid": BOARD_META,
                                        children: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                "in\xa0",
                                                ancestors
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            });
        }
    });
}
export const BoardGalleryCard = /*#__PURE__*/ memo(_BoardGalleryCard);
